// ECharts option for the sales funnel with smooth (curved) sides, drawn with a custom series.
// Kept free of Vue and ECharts imports.

// Shades of the admin primary green (#64a70b), darkest at the top
const COLORS = ["#4f8609", "#64a70b", "#8cc04a", "#b9d98f"];
const LABEL_COLORS = ["#ffffff", "#ffffff", "#24400a", "#24400a"];
const FONT = "'Open Sans', sans-serif";

// Layout, as shares of the chart size
const FUNNEL_LEFT = 0.04;
const FUNNEL_WIDTH = 0.56;
const MIN_WIDTH = 0.14; // narrowest segment, as a share of the funnel width
const LAST_TAPER = 0.72; // bottom of the last segment, as a share of its top
const GAP = 6;
const PADDING = 8;

export const formatNumber = (value) => Number(value || 0).toLocaleString("uk-UA");

export const isEmptyFunnel = (steps) => !steps.length || !Number(steps[0].users);

// Segment with straight top and bottom edges and S-shaped sides. The sides are vertical
// where segments meet, so the whole funnel reads as one smooth outline.
const segmentPath = (cx, y, height, topWidth, bottomWidth) => {
  const topLeft = cx - topWidth / 2;
  const topRight = cx + topWidth / 2;
  const bottomLeft = cx - bottomWidth / 2;
  const bottomRight = cx + bottomWidth / 2;
  const bottom = y + height;
  const c1 = y + height * 0.55;
  const c2 = y + height * 0.45;
  return (
    `M${topLeft} ${y} L${topRight} ${y} ` +
    `C${topRight} ${c1} ${bottomRight} ${c2} ${bottomRight} ${bottom} ` +
    `L${bottomLeft} ${bottom} ` +
    `C${bottomLeft} ${c2} ${topLeft} ${c1} ${topLeft} ${y} Z`
  );
};

// steps: [{ title, users, fromStart, fromPrevious }]
export const buildFunnelOption = (steps) => {
  const empty = isEmptyFunnel(steps);
  const max = Number(steps[0] && steps[0].users) || 1;
  const count = steps.length;

  const renderItem = (params, api) => {
    const index = params.dataIndex;
    const width = api.getWidth();
    const height = api.getHeight();
    const funnelWidth = width * FUNNEL_WIDTH;
    const cx = width * FUNNEL_LEFT + funnelWidth / 2;
    const segmentHeight = (height - 2 * PADDING - (count - 1) * GAP) / count;
    const y = PADDING + index * (segmentHeight + GAP);
    const widthOf = (i) => Math.max(funnelWidth * MIN_WIDTH, (funnelWidth * (Number(steps[i].users) || 0)) / max);
    const topWidth = widthOf(index);
    const bottomWidth = index < count - 1 ? widthOf(index + 1) : topWidth * LAST_TAPER;
    const middle = y + segmentHeight / 2;
    const step = steps[index];
    const labelX = width * FUNNEL_LEFT + funnelWidth + 24;
    const edgeX = cx + (topWidth + bottomWidth) / 4;

    return {
      type: "group",
      children: [
        {
          type: "path",
          shape: { pathData: segmentPath(cx, y, segmentHeight, topWidth, bottomWidth) },
          style: { fill: COLORS[index % COLORS.length] },
          emphasis: { style: { opacity: 0.85 } },
        },
        {
          type: "text",
          silent: true,
          style: {
            x: cx,
            y: middle,
            text: formatNumber(step.users),
            fill: LABEL_COLORS[index % LABEL_COLORS.length],
            font: `600 14px ${FONT}`,
            align: "center",
            verticalAlign: "middle",
          },
        },
        {
          type: "line",
          silent: true,
          shape: { x1: edgeX + 6, y1: middle, x2: labelX - 6, y2: middle },
          style: { stroke: "#c3c2b7", lineWidth: 1 },
        },
        {
          type: "text",
          silent: true,
          style: {
            x: labelX,
            y: middle,
            text: `{title|${step.title}}\n{rate|${step.fromStart} від початку}`,
            verticalAlign: "middle",
            rich: {
              title: { fontSize: 13, fontWeight: 600, fontFamily: FONT, fill: "#2c2c2a", lineHeight: 18 },
              rate: { fontSize: 12, fontFamily: FONT, fill: "#6b6e75", lineHeight: 16 },
            },
          },
        },
      ],
    };
  };

  return {
    tooltip: {
      trigger: "item",
      textStyle: { fontFamily: FONT },
      formatter: ({ data: { step } }) =>
        `${step.title}<br/>Користувачів: <b>${formatNumber(step.users)}</b>` +
        `<br/>% від початку: ${step.fromStart}` +
        `<br/>% від попереднього етапу: ${step.fromPrevious}`,
    },
    series: [
      {
        type: "custom",
        coordinateSystem: "none",
        renderItem,
        data: empty ? [] : steps.map((step, index) => ({ value: [index, Number(step.users) || 0], step })),
      },
    ],
  };
};
